import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadOutlined, DeleteOutlined, DownloadOutlined } from "us.icons";
import { IUpload } from "./Interfaces";
import { Formik } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import {
  ImportFileParams,
  MaxFileSize,
  POSTAL_CODE_TABLE_COLUMN_HEADERS,
} from "us.common/constants";
import { getExcelDataByImportType, downloadCSV } from "./functions";
import {
  $Upload,
  $Typography,
  $Popconfirm,
  $Message,
  $Button,
  $Form,
  $Select,
} from "us.common/components";

const { Dragger } = $Upload;
const { Text } = $Typography;

/**
 * @description - Import from file common main view
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3119120385/Import+Court+Instances+from+file+UI+Implementation
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3107028995/Import+Postal+Areas+from+file+UI+Implementation
 * @author Kaushalya Sandamali <kaushalyas@unicorn-solutions.com>
 * @since 04/10/2022
 */
const ImportFromFile: React.FC<PropsFromRedux & IUpload> = (props) => {
  const { t } = useTranslation();

  const [fileList, setFileList] = useState<any>([]);
  const {
    countries,
    currentCurrency,
    importType,
    courtTypes,
    isCourtDataImporting,
    isPostalCodeImporting,
    systemCountryCode,
    onClose,
    handleImportAction,
  } = props;

  /**
   * @description define dragger props
   */
  const draggerProps = {
    beforeUpload: (file: any, list: any) => {
      const newList = list;
      if (file.size / 1024 / 1024 > 5) {
        $Message.error(t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED"));
      } else if (file.type !== "text/csv" && file.type !== "application/vnd.ms-excel") {
        $Message.error(
          t("US.COMMON:COMMON.ONLY_CSV_FORMATTED_FILES_ARE_ALLOWED")
        );
      } else {
        newList.forEach((item: any) => {
          const isListMax = item.size / 1024 / 1024 > 5;
          if (isListMax) {
            item.status = "error";
            item.response = t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED");
          }
        });
        setFileList(newList);
      }
      return false;
    },
    accept: ".csv",
    multiple: false,
    fileList,
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: (file: any) => (
        <$Popconfirm
          title={t("US.COMMON:COMMON.SURE_TO_DELETE_?")}
          data-testid="remove-pop-confirm"
          okText={t("US.COMMON:COMMON.YES")}
          cancelText={t("US.COMMON:COMMON.NO")}
          onConfirm={() => {
            const index = fileList.indexOf(file);
            fileList?.splice(index, 1);
            setFileList([...fileList]);
          }}
          placement="right"
        >
          <DeleteOutlined data-testid="remove-icon" />
        </$Popconfirm>
      ),
    },
    maxCount: 1,
  };

  /**
   * @description handle Postal code item menu actions
   * @param action - action of clicked menu icon
   * @param record - selected postal code item for action
   */
  const handleOnSubmit = (values: any) => {
    handleImportAction({
      file: fileList,
      fileName: fileList[0].name,
      countryCode: values?.countryName,
      courtType:
        importType === ImportFileParams.COURT_FILE_IMPORT
          ? values?.courtType ?? -1
          : "",
    });
  };

  /**
   * @description Translate column headers
   */
  const getPostalAreaTableHeaders = () => {
    const headers: any = [];
    POSTAL_CODE_TABLE_COLUMN_HEADERS.map((item: any) => headers.push(t(item)));
    return headers;
  };

  return (
    <Formik
      initialValues={{ countryName: systemCountryCode }}
      onSubmit={(values: any, actions: any) => {
        handleOnSubmit(values);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...rest
      }: any) => (
        <>
          <$Form>
            <div className="mb-3">
              <$Select
                size="small"
                name="countryName"
                className="w-100"
                formitem={{
                  label: t("US.COMMON:COMMON.COUNTRY"),
                }}
                allOption={false}
                dropdownMatchSelectWidth={false}
                options={countries.data}
                loading={countries.isLoading}
                disabled={countries.isLoading}
                optionValue="code"
                optionText="name"
                defaultValue={systemCountryCode}
              />
              {importType === ImportFileParams.COURT_FILE_IMPORT && (
                <$Select
                  size="small"
                  name="courtType"
                  className="w-100"
                  formitem={{
                    label: t("US.COMMON:COMMON.COURT_TYPE"),
                  }}
                  allOption={false}
                  dropdownMatchSelectWidth={false}
                  options={
                    courtTypes?.filter(
                      (item: any) =>
                        item.courtTypeId == 2 || item.courtTypeId == 3
                    ) ?? []
                  }
                  optionValue="courtTypeId"
                  optionText="name"
                  placeholder={t("US.COMMON:COMMON.SELECT_COURT_TYPE")}
                />
              )}
            </div>
            <div className="mb-4">
              <$Button
                type="dashed"
                size="small"
                icon={<DownloadOutlined />}
                disabled={
                  importType === ImportFileParams.COURT_FILE_IMPORT &&
                  values?.courtType == undefined
                }
                block
                onClick={() => {
                  downloadCSV(
                    importType === ImportFileParams.POSTAL_CODE_FILE_IMPORT
                      ? [getPostalAreaTableHeaders()]
                      : getExcelDataByImportType(values?.courtType),
                    importType === ImportFileParams.POSTAL_CODE_FILE_IMPORT
                      ? "Postal Area File Format.csv"
                      : "Court File Format.csv",
                    currentCurrency == currentCurrency
                      ? ";"
                      : ","
                  );
                }}
              >
                {t("US.COMMON:COMMON.DOWNLOAD_SAMPLE_FILE_FORMAT")}
              </$Button>
            </div>
            <div>
              <div
                className={
                  fileList.length >= 1
                    ? "mt-4 mb-5 upload-disable"
                    : "mt-4 mb-5"
                }
              >
                <Dragger
                  {...draggerProps}
                  name="fileUpload"
                  data-testid="upload-dragger"
                  onChange={(file: any) => {
                    const formData = new FormData();
                    formData.append("files[]", file);
                  }}
                >
                  <p
                    className="ant-upload-drag-icon mb-0 mr-3"
                    data-testid="upload-btn"
                  >
                    <UploadOutlined />
                  </p>
                  <div>
                    <p className="ant-upload-text">
                      {t(
                        "US.COMMON:COMMON.CLICK_OR_DRAG_FILE_TO_THIS_AREA_TO_UPLOAD"
                      )}
                    </p>
                    <p className="ant-upload-hint">
                      {t("US.COMMON:COMMON.SUPPORT_FOR_A_SINGLE_FILE_UPLOAD")}
                    </p>
                    <div>
                      <Text type="secondary">
                        {t("US.COMMON:COMMON.ONLY_ALLOWED") +
                          ` csv ` +
                          `(${t(
                            "US.COMMON:COMMON.MAXIMUM_UPLOAD_SIZE"
                          )} ${MaxFileSize} MB)`}
                      </Text>
                    </div>
                  </div>
                </Dragger>
              </div>
            </div>
          </$Form>
          <div className="drawer-footer-fixed align-content-center">
            <div className="ml-auto">
              <$Button
                className="mr-2"
                disabled={
                  !rest.isValid ||
                  fileList?.length == 0 ||
                  (importType === ImportFileParams.COURT_FILE_IMPORT &&
                    values?.courtType == undefined)
                }
                loading={isPostalCodeImporting || isCourtDataImporting}
                onClick={() => handleSubmit()}
                type="primary"
              >
                {t("US.COMMON:COMMON.SAVE")}
              </$Button>
              <$Button onClick={() => onClose()}>
                {t("US.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { entityDetails, common } = state;
  const { countries } = entityDetails;
  const { currentDateFormat, currentLanguage, currentCurrency, systemCountryCode } = common;

  return {
    countries,
    currentDateFormat,
    currentLanguage,
    currentCurrency,
    systemCountryCode
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ImportFromFile);
